import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  BookOpen, 
  Search, 
  ArrowRight,
  GraduationCap,
  Brain,
  Calculator,
  Sparkles,
  Target,
  Award,
  Trophy,
  Users,
  HelpCircle,
  FileText,
  MessageCircle,
  Video,
  MonitorPlay,
  UsersRound,
  Globe,
  Building,
  PlayCircle
} from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useMemo } from "react";

interface Platform {
  name: string;
  slug: string;
  description: string;
  category: string;
  icon: typeof BookOpen;
}

const platforms: Platform[] = [
  // General Academic Support
  {
    name: "Khan Academy",
    slug: "khan-academy",
    description: "Free nonprofit learning platform with thousands of video lessons and practice exercises across math, science, history, and more.",
    category: "General Academic Support",
    icon: GraduationCap,
  },
  {
    name: "Quizlet",
    slug: "quizlet",
    description: "Popular study tool centered on user-generated flashcards and interactive practice activities for memorization.",
    category: "General Academic Support",
    icon: Brain,
  },
  {
    name: "Wolfram Alpha",
    slug: "wolfram-alpha",
    description: "Computational knowledge engine for math, science, and factual queries with step-by-step solutions.",
    category: "General Academic Support",
    icon: Calculator,
  },
  {
    name: "SparkNotes",
    slug: "sparknotes",
    description: "Long-standing resource for literature study guides with chapter summaries, character analyses, and No Fear Shakespeare translations.",
    category: "General Academic Support",
    icon: BookOpen,
  },
  // Test Preparation
  {
    name: "Magoosh",
    slug: "magoosh",
    description: "Affordable, self-paced test prep for GRE, GMAT, LSAT, SAT, ACT with video lessons and extensive practice questions.",
    category: "Test Preparation",
    icon: Target,
  },
  {
    name: "Kaplan Test Prep",
    slug: "kaplan",
    description: "Premium test preparation courses for SAT, ACT, GRE, GMAT, LSAT, MCAT with live classes and private tutoring options.",
    category: "Test Preparation",
    icon: Award,
  },
  {
    name: "The Princeton Review",
    slug: "princeton-review",
    description: "Leading test prep provider with rigorous curricula, score improvement guarantees, and expert instructors.",
    category: "Test Preparation",
    icon: Trophy,
  },
  {
    name: "Fiveable",
    slug: "fiveable",
    description: "Community-based platform for AP exam prep with live study sessions, cram nights, and collaborative study rooms.",
    category: "Test Preparation",
    icon: Users,
  },
  // Tutoring & Homework Help
  {
    name: "Chegg",
    slug: "chegg",
    description: "On-demand homework help with step-by-step textbook solutions, expert Q&A, math solver, and writing tools.",
    category: "Tutoring & Homework Help",
    icon: HelpCircle,
  },
  {
    name: "Course Hero",
    slug: "course-hero",
    description: "Access millions of course-specific study materials, lecture notes, past exams, and 24/7 homework help from tutors.",
    category: "Tutoring & Homework Help",
    icon: FileText,
  },
  {
    name: "Brainly",
    slug: "brainly",
    description: "Crowd-sourced homework Q&A platform where students help other students with academic questions.",
    category: "Tutoring & Homework Help",
    icon: MessageCircle,
  },
  // Collaborative Study Tools
  {
    name: "Focusmate",
    slug: "focusmate",
    description: "Virtual body-doubling platform that pairs you with a partner for live, webcam-based co-working sessions.",
    category: "Collaborative Study Tools",
    icon: Video,
  },
  {
    name: "StudyStream",
    slug: "studystream-platform",
    description: "24/7 virtual study rooms where hundreds of students join via video to study together in real time.",
    category: "Collaborative Study Tools",
    icon: MonitorPlay,
  },
  {
    name: "Study Together",
    slug: "study-together",
    description: "Massive Discord community with 24/7 focus rooms, free tutoring, and gamified study tracking.",
    category: "Collaborative Study Tools",
    icon: UsersRound,
  },
  // Skill-Based Learning
  {
    name: "Coursera",
    slug: "coursera",
    description: "Leading MOOC platform with 5,000+ courses from top universities like Stanford and Yale, plus professional certificates from Google and IBM.",
    category: "Skill-Based Learning",
    icon: Globe,
  },
  {
    name: "edX",
    slug: "edx",
    description: "University-level courses from Harvard and MIT with MicroMasters programs that can count toward full degrees.",
    category: "Skill-Based Learning",
    icon: Building,
  },
  {
    name: "Udemy",
    slug: "udemy",
    description: "Vast course marketplace with 200,000+ affordable courses on any topic, from programming to personal development.",
    category: "Skill-Based Learning",
    icon: PlayCircle,
  },
];

const categories = ["All", "General Academic Support", "Test Preparation", "Tutoring & Homework Help", "Collaborative Study Tools", "Skill-Based Learning"];

const OnlineStudy = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredPlatforms = useMemo(() => {
    return platforms.filter(platform => {
      const matchesSearch = platform.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          platform.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === "All" || platform.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Online Study Platforms - Study Online for Real Skills & Careers | CSW",
    "description": "Study online for real skills, real careers, and real progress. Discover top study platforms, build career-focused skills, and stay motivated with CSW.",
    "url": "https://csw.live/online-study/",
    "inLanguage": "en",
    "dateModified": new Date().toISOString().split('T')[0],
    "keywords": "online study, study online, online learning, career-focused learning, study platforms, virtual study rooms, online education",
    "publisher": {
      "@type": "Organization",
      "name": "CSW",
      "url": "https://csw.live"
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": platforms.map((platform, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": platform.name,
        "url": `https://csw.live/online-study/${platform.slug}/`
      }))
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What makes online studying effective?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Online studying is most effective when it includes structure, interaction, and human connection. Tools like CSW provide virtual study rooms, accountability partners, and focus timers to keep learners motivated and consistent."
        }
      },
      {
        "@type": "Question",
        "name": "Can I study online for career advancement?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, online learning platforms offer career-focused programs in web development, business, marketing, HR, and professional development. CSW helps career learners stay disciplined with productivity tools and study communities."
        }
      },
      {
        "@type": "Question",
        "name": "How do virtual study rooms help with online learning?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Virtual study rooms provide quiet accountability in digital classrooms, peer collaboration without distractions, and a sense of shared effort that improves motivation and focus during online learning sessions."
        }
      },
      {
        "@type": "Question",
        "name": "Is CSW suitable for all types of online programs?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "CSW supports full-time and part-time online programs, certificates, diplomas, degrees, and asynchronous courses. The platform adapts to your schedule with 24/7 study rooms and flexible productivity tools."
        }
      },
      {
        "@type": "Question",
        "name": "What tools does CSW offer for online students?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "CSW offers Study Stream for live study rooms, Pomodoro timers for focused sessions, task management for deadline tracking, flashcards for memorization, and a calm interface designed for productivity without overstimulation."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Online Study Platforms - Study Online for Real Skills & Careers"
        description="Study online for real skills, real careers, and real progress. Discover top study platforms, build career-focused skills, and stay motivated with CSW."
        keywords="online study, study online, online learning, career-focused learning, study platforms, virtual study rooms, online education, professional development"
        canonicalUrl="/online-study/"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">STUDY PLATFORM REVIEWS</span>
            </div>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Online <span className="text-gradient">Study Platforms</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Discover and compare the best online study tools and learning platforms for academic success.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search platforms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-6 text-lg bg-card border-border"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? "bg-primary text-primary-foreground" : ""}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Grid */}
      <section className="py-12 relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {filteredPlatforms.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPlatforms.map((platform) => {
                  const Icon = platform.icon;
                  return (
                    <Link key={platform.slug} to={`/online-study/${platform.slug}/`}>
                      <Card className="group h-full bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 cursor-pointer">
                        <CardContent className="p-6 space-y-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <span className="text-xs font-medium text-primary/70">{platform.category}</span>
                            <h3 className="text-xl font-semibold text-foreground font-display mt-1">
                              {platform.name}
                            </h3>
                          </div>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {platform.description}
                          </p>
                          <div className="flex items-center gap-2 text-primary font-medium">
                            Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">No platforms found matching your search.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Study Online Content Section */}
      <section className="py-16 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto prose prose-lg dark:prose-invert">
            <h2 id="real-skills" className="font-display text-3xl font-bold text-foreground mb-6">
              Study Online for Real Skills, Real Careers, and Real Progress
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Studying online today goes far beyond watching videos or completing assignments alone. For students to truly succeed, online learning needs structure, interaction, and human connection — not just flexibility.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              CSW exists to support the real online student experience: helping learners stay focused, motivated, and consistent while studying online for academics, careers, and professional growth. Our <Link to="/study-stream/" className="text-primary hover:underline">Study Stream</Link> and <Link to="/study-with-me/" className="text-primary hover:underline">Study With Me</Link> features create the accountability online learners need.
            </p>

            <h2 id="career-focused" className="font-display text-3xl font-bold text-foreground mb-6">
              Career-Focused Online Learning That Leads Somewhere
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Many students now study online to meet <a href="https://www.weforum.org/publications/the-future-of-jobs-report-2023/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">job market demands</a> and build practical, in-demand skills. According to the <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">U.S. Bureau of Labor Statistics</a>, online programs increasingly focus on real-world learning, including:
            </p>
            <ul className="text-muted-foreground space-y-2 mb-6 list-disc list-inside">
              <li>Web design and development</li>
              <li>Human resources and leadership</li>
              <li>Interior decorating and creative programs</li>
              <li>Business, marketing, and operations</li>
              <li>Professional development and micro-credentials</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Whether you're earning an acknowledgement of completion, a certificate, or advancing through continuous professional learning, online education offers flexibility — but progress still requires discipline and consistency. CSW provides a structured environment where career-driven learners can show up daily, practice skills, and stay accountable using our <Link to="/study-timer/" className="text-primary hover:underline">Pomodoro Timer</Link> and <Link to="/task-management/" className="text-primary hover:underline">Task Management</Link> tools.
            </p>

            <h2 id="every-program" className="font-display text-3xl font-bold text-foreground mb-6">
              One Online Study Space for Every Program Type
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Online learners aren't all the same — and CSW is designed to support every format. Perfect for students in:
            </p>
            <ul className="text-muted-foreground space-y-2 mb-6 list-disc list-inside">
              <li>Full-time and part-time online programs</li>
              <li>Online certificates and graduate certificates</li>
              <li>Diplomas and advanced diplomas</li>
              <li>Degrees and professional programs</li>
              <li>Asynchronous and self-paced online courses</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-8">
              No matter how your program is delivered, CSW gives you a reliable digital study space that fits your schedule — not the other way around. Explore our <Link to="/online-workspace/" className="text-primary hover:underline">Online Workspace</Link> for a distraction-free environment.
            </p>

            <h2 id="student-experiences" className="font-display text-3xl font-bold text-foreground mb-6">
              Real Student Experiences Matter
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Online learning works best when students feel connected — not isolated. Research from <a href="https://www.sciencedirect.com/journal/computers-and-education" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Computers & Education Journal</a> shows that studying alongside others improves motivation, focus, and confidence. Students consistently value:
            </p>
            <ul className="text-muted-foreground space-y-2 mb-6 list-disc list-inside">
              <li>Peer collaboration without distractions</li>
              <li>Quiet accountability in digital classrooms</li>
              <li>Interactive learning tools that support focus</li>
              <li>Flexible schedules that still feel structured</li>
              <li>A sense of shared effort, even remotely</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-8">
              CSW recreates the feeling of a real study room online. You see others studying, stay present in your session, and build habits through shared focus — without forced discussion or interruptions. Try <Link to="/study-with-strangers/" className="text-primary hover:underline">studying with strangers</Link> or <Link to="/study-with-friends/" className="text-primary hover:underline">study with friends</Link> for peer accountability.
            </p>

            <h2 id="support-beyond" className="font-display text-3xl font-bold text-foreground mb-6">
              Support Beyond Lectures and Videos
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Successful online learners rely on more than course content. The <a href="https://learningcenter.unc.edu/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">UNC Learning Center</a> emphasizes that students need support systems that help with:
            </p>
            <ul className="text-muted-foreground space-y-2 mb-6 list-disc list-inside">
              <li>Self-motivation and discipline</li>
              <li>Time management skills</li>
              <li>Staying engaged without physical classrooms</li>
              <li>Managing multiple deadlines</li>
              <li>Balancing school, work, and personal life</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-8">
              CSW complements traditional online support services like advising and teacher support by offering peer-driven accountability. Studying with others — even silently — helps learners stay committed and reduce burnout over time. Read our blog on <Link to="/blog/how-to-stop-procrastination/" className="text-primary hover:underline">how to stop procrastination</Link> and <Link to="/blog/science-behind-study-with-me/" className="text-primary hover:underline">the science behind study with me</Link>.
            </p>

            <h2 id="interactive-learning" className="font-display text-3xl font-bold text-foreground mb-6">
              Interactive Learning Without Overstimulation
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Many online platforms overwhelm students with notifications, chats, and endless content. CSW is intentionally different. Inside CSW, you'll find:
            </p>
            <ul className="text-muted-foreground space-y-2 mb-6 list-disc list-inside">
              <li>Live online study rooms</li>
              <li>Focus timers and session tracking</li>
              <li>Tools that encourage active learning</li>
              <li>A calm, minimal interface built for productivity</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-8">
              It supports digital fluency skills without turning studying into another social feed. Explore our <Link to="/flash-cards/" className="text-primary hover:underline">flashcards</Link> and <Link to="/quizzes/" className="text-primary hover:underline">quizzes</Link> for active recall practice.
            </p>

            <h2 id="global-learners" className="font-display text-3xl font-bold text-foreground mb-6">
              Built for Global, Modern Online Learners
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              CSW supports learners across time zones and backgrounds, including:
            </p>
            <ul className="text-muted-foreground space-y-2 mb-6 list-disc list-inside">
              <li>International students</li>
              <li>Working professionals</li>
              <li>Career switchers</li>
              <li>Remote and hybrid learners</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-8">
              If your education is online, your study environment should be too — structured, supportive, and human. CSW is available in <Link to="/kr/" className="text-primary hover:underline">Korean</Link>, <Link to="/jp/" className="text-primary hover:underline">Japanese</Link>, <Link to="/cn/" className="text-primary hover:underline">Chinese</Link>, and <Link to="/vi/" className="text-primary hover:underline">Vietnamese</Link>.
            </p>

            <h2 id="confidence" className="font-display text-3xl font-bold text-foreground mb-6">
              Study Online With Confidence and Consistency
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Online learning opens doors — but showing up consistently is what gets you through them. CSW gives you:
            </p>
            <ul className="text-muted-foreground space-y-2 mb-6 list-disc list-inside">
              <li>A focused digital classroom</li>
              <li>Motivation through shared presence</li>
              <li>Structure without pressure</li>
              <li>Tools that support real learning</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-4xl mx-auto p-12 sm:p-16 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/50 border border-border mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">FREE STUDY PLATFORM</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Study Online With Focus and Momentum
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join CSW for free and study online with confidence, structure, and a global community of focused learners.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Join CSW for Free <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OnlineStudy;
